import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "rounded-lg border border-dashed border-muted bg-muted/10 p-4" }
const _hoisted_2 = {
  key: 0,
  class: "relative flex w-full items-center justify-center overflow-hidden rounded-md border bg-white",
  style: {"max-height":"200px"}
}
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 1,
  class: "flex flex-col items-center gap-2 py-6"
}
const _hoisted_5 = {
  key: 2,
  class: "absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px]"
}
const _hoisted_6 = {
  key: 0,
  class: "mt-2 text-xs text-red-600"
}
const _hoisted_7 = {
  key: 1,
  class: "mt-2 text-xs text-muted-foreground"
}

import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { getCompanyLogoUploadUrl } from '@/api/company/profile'

const MAX_SIZE = 2 * 1024 * 1024 // 2MB

export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileLogoUploader',
  props: {
    currentLogo: {}
  },
  emits: ["uploaded", "removed", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emits = __emit

const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const errorMessage = ref<string | null>(null)
const preview = ref<string | null>(props.currentLogo ?? null)

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

watch(
  () => props.currentLogo,
  (value) => {
    if (!isUploading.value) {
      preview.value = value ?? null
    }
  }
)

const hasPreview = computed(() => !!preview.value)

function openFilePicker() {
  fileInputRef.value?.click()
}

function onDragOver() {
  isDragging.value = true
}

function onDragLeave() {
  isDragging.value = false
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) handleFile(file)
}

function onFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) handleFile(file)
}

async function handleFile(file: File) {
  errorMessage.value = null

  if (!ALLOWED_TYPES.includes(file.type)) {
    errorMessage.value = 'Unsupported file type (only PNG, JPG, WEBP allowed).'
    return
  }

  if (file.size > MAX_SIZE) {
    errorMessage.value = 'File too large (max 2MB).'
    return
  }

  try {
    isUploading.value = true
    const { data } = await getCompanyLogoUploadUrl(file.type)

    await axios.put(data.uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    })

    const url = `${data.publicUrl}?t=${Date.now()}`
    preview.value = url
    emits('uploaded', { url, fileType: file.type })
  } catch (error) {
    console.error('Failed to upload company logo', error)
    errorMessage.value = 'Upload failed. Please try again.'
  } finally {
    isUploading.value = false
  }
}

function handleRemove() {
  preview.value = null
  emits('removed')
  if (fileInputRef.value) fileInputRef.value.value = ''
}

function handleClose() {
  emits('close')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", { class: "mb-3 flex items-center justify-between" }, [
      _cache[0] || (_cache[0] = _createElementVNode("div", null, [
        _createElementVNode("h3", { class: "text-sm font-medium" }, "Upload New Logo"),
        _createElementVNode("p", { class: "text-xs text-muted-foreground" }, " PNG, JPG, or WEBP • Max 2MB ")
      ], -1)),
      _createElementVNode("button", {
        type: "button",
        class: "text-xs text-muted-foreground hover:text-foreground",
        onClick: handleClose
      }, " Cancel ")
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["relative flex w-full flex-col items-center justify-center rounded-md border-2 border-dashed bg-background p-4 text-center transition", isDragging.value ? 'border-brand/60' : 'border-muted-foreground/30']),
      onDragover: _withModifiers(onDragOver, ["prevent"]),
      onDragleave: _withModifiers(onDragLeave, ["prevent"]),
      onDrop: _withModifiers(onDrop, ["prevent"])
    }, [
      (hasPreview.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("img", {
              src: preview.value,
              alt: "Company logo preview",
              class: "max-h-[180px] max-w-full object-contain"
            }, null, 8, _hoisted_3),
            _createElementVNode("button", {
              type: "button",
              class: "absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/80",
              onClick: handleRemove,
              "aria-label": "Remove logo"
            }, " ✕ ")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _cache[3] || (_cache[3] = _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              class: "h-8 w-8 text-muted-foreground/60",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "1.5",
                d: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-9-12v12m0 0L7.5 13.5m4.5 2.25L16.5 13.5"
              })
            ], -1)),
            _createElementVNode("p", { class: "text-sm text-muted-foreground" }, [
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "font-medium text-foreground" }, "Drop", -1)),
              _cache[2] || (_cache[2] = _createTextVNode(" your logo here or ")),
              _createElementVNode("button", {
                type: "button",
                class: "underline",
                onClick: openFilePicker
              }, "browse")
            ])
          ])),
      (isUploading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[4] || (_cache[4] = [
            _createElementVNode("svg", {
              class: "h-6 w-6 animate-spin",
              viewBox: "0 0 24 24",
              fill: "none"
            }, [
              _createElementVNode("circle", {
                class: "opacity-25",
                cx: "12",
                cy: "12",
                r: "10",
                stroke: "currentColor"
              }),
              _createElementVNode("path", {
                class: "opacity-75",
                d: "M4 12a8 8 0 018-8",
                stroke: "currentColor"
              })
            ], -1),
            _createElementVNode("span", { class: "ml-2 text-sm" }, "Uploading…", -1)
          ])))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        ref_key: "fileInputRef",
        ref: fileInputRef,
        type: "file",
        accept: "image/png,image/jpeg,image/webp",
        class: "hidden",
        onChange: onFileChange
      }, null, 544)
    ], 34),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(errorMessage.value), 1))
      : (_openBlock(), _createElementBlock("p", _hoisted_7, " Recommended square logo (e.g., 500×500). Non-square images will be letterboxed (not cropped). "))
  ]))
}
}

})